import ComponentManager from '@/Management/ComponentManager';
import Factory from '@/Factories/Factory';
import ControlFactoryTuple from '@/Factories/ControlFactoryTuple';
import LinkViewModelType from '@/ViewModels/Controls/LinkViewModel';
import LabelViewModelType from '@/ViewModels/Controls/LabelViewModel';
import ImageViewModelType from '@/ViewModels/Controls/ImageViewModel';
import LinkType from '@/Controls/Link';
import LabelType from '@/Controls/Label';
import ResourceManager from '@/Management/ResourceManager';
import ControlViewModel from '@/ViewModels/ControlViewModel';
import Control from '@/Controls/Control';
import type DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import { LabelPosition, ColumnSpan, IControlFactoryConstructorParameter } from '#/CoreFrameInterfaces';
import { FilterInfo, ITupleProvider } from '#/DefaultViewExtensionInterfaces';
import TypeConverter from '@/Utility/TypeConverter';
import LocatorHelper from '@/Utility/LocatorHelper';
import Utilities from '@/Utility/Utilities';
import AsyncTypeGuards from '@/Utility/AsyncTypeGuards';
import Context from '@/ViewModels/Context';
import { IDVGItemDTO } from "coreframetypings/dto";
import GlobalConfiguration from "@/Management/GlobalConfiguration";

/**
* Repräsentiert die Oberklasse aller ControlFactories
*/
export default class ControlFactory extends Factory implements ITupleProvider<ControlFactoryTuple> {

    protected LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModelType>('LinkViewModel');
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelType>('LabelViewModel');
    protected ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelType>('ImageViewModel');
    protected Link = ComponentManager.getControl<typeof LinkType>('Link');
    protected Label = ComponentManager.getControl<typeof LabelType>('Label');

    /**
    * Zugänglich für automatisches Disposing
    */
    public viewModel: ControlViewModel;

    /**
     * Zugänglich für automatisches Disposing
     */
    public control: Control; // beinhaltet z.B. Instanz von TextBox(-Control)
    
    /** 
     * Beinhaltet das aktuelle DetailViewItemDTO
     */
    public detailViewItem: IDVGItemDTO;

    /** 
     * Der Kontext aus dem die Factory erzeugt wurde
     */
    public context: DetailViewContext;

    /** 
     * Gibt den "Nachbarn" der Factory im Layout an
     */
    public layout: string;

    /**
     * Definiert die Sichtbarkeit des Controls
     */
    public isDisplayed: KnockoutObservable<boolean>;

    /** 
     * Gibt den "Nachbarn" der Factory im Layout an
     */
    public neighborOf: string;

    /**
     * Gibt an, ob die Factory fokussierbar ist (Keyboard-Navigation)
     */
    public isFocusable: KnockoutObservable<boolean>;

    /** 
     * Definiert die Position des Labels im Layout
     */
    public labelPosition: LabelPosition;

    /** 
     * Definiert die Breite der Factory im Layout
     */
    public colspan: ColumnSpan;

    /** 
    * Gibt an, ob die Factory sichtbar ist
    */
    public isVisible: KnockoutObservable<boolean>;

    /** 
    * Gibt an, ob sich die ControlFactory in einer SummaryView befindet
    */
    public isSummaryViewMode: boolean;

    /** 
    * Gibt an, ob die Factory ein Pflichtfeld ist
    */
    public isRequired: KnockoutObservable<boolean>;

    /** 
    * Gibt an, ob die Factory derzeit geladen wird
    */
    public isBusy: KnockoutObservable<boolean>;

    /**
    * Ein Fehlertext an der Factory
    */
    public invalidationText: KnockoutObservable<string>;

    /** 
    * Gibt an, ob die Factory valide ist
    */
    public isValid: KnockoutObservable<boolean>;

    /** 
    * Gibt an, ob die Factory enabled ist
    */
    public isEnabled: KnockoutComputed<boolean>;

    /**
     * Enthält das ControlFactoryTuple
     */
    public tuple: ControlFactoryTuple;

    /**
     * Enthält das LabelViewModel
     */
    public labelViewModel: LabelViewModelType;

    /**
     * Enthält das Label
     */
    public label: LabelType;

    /**
     * Gibt an, ob die Client-seitige Validierung ignoriert werden soll.
     * Default: False
     */
    public readonly ignoreClientValidation: KnockoutObservable<boolean>;

    /**
     * Registrierte Validierungen für dieses Control
     * Es ist immer eine Funktion, die den zu validierenden Wert zurückgibt
     * und eine Validierungsfunktion erforderlich
     */
    private readonly validationFunctions: Array<{
        value: () => any;
        validate: (value: any) => boolean;
        validationText?: (value: any) => string;
    }>;

    /**
     * Gibt an, ob die serverseitige Validierung erfolgreich war
     */
    private isValidByServer: boolean;

    constructor(params: IControlFactoryConstructorParameter) {
        super(params);
        this.isControlFactory = true;

        this.viewModel = null;
        this.control = null; // beinhaltet z.B. Instanz von TextBox(-Control)
        this.detailViewItem = this.paramOrDefault(params.detailViewItem, null);
        this.context = this.paramOrDefault(params.context, null);
        this.layout = this.paramOrDefault(params.layout, 'OneColumnLayout');
        this.isDisplayed = this.observableOrDefault(params.isDisplayed, true);
        this.neighborOf = this.paramOrDefault(params.neighborOf, null);
        this.isFocusable = this.observableOrDefault(params.isFocusable, true);
        this.labelPosition = this.paramOrDefault(params.labelPosition, 'Left');
        this.colspan = this.paramOrDefault(params.colspan, 'One');
        
        this.ignoreClientValidation = this.observableOrDefault(false);
        
        if (params.detailViewItem.hasOwnProperty("isVisible"))
            this.isVisible = this.observableOrDefault(params.detailViewItem.isVisible, true).extend({ notify: 'always' });
        else
            this.isVisible = this.observableOrDefault(params.isVisible, true).extend({ notify: 'always' });

        this.isSummaryViewMode = this.paramOrDefault(params.isSummaryViewMode, false);
        this.isRequired = this.observableOrDefault(params.detailViewItem.required, false);
        this.isBusy = this.observableOrDefault(params.isBusy, false);
        this.invalidationText = this.observableOrDefault(params.invalidationText, '');
        this.isValid = this.observableOrDefault(params.isValid, true);

        const originalIsEnabled = this.observableOrDefault(params.isEnabled, true);
        const detailViewItemIsEnabled = this.observableOrDefault(params.detailViewItem.isEnabled, true);
        const detailViewItemIsReadonly = this.observableOrDefault(params.detailViewItem.guiReadOnly, false);

        this.isEnabled = this.definePureComputed({
            read: () => {
                const originalEnabled = originalIsEnabled();
                const detailViewItemEnabled = detailViewItemIsEnabled();
                const detailViewItemReadonly = detailViewItemIsReadonly();
                const locked = this.context?.hasOwnProperty('isLocked') && this.context.isLocked();
                return originalEnabled && detailViewItemEnabled && !detailViewItemReadonly && !locked;
            },
            write: (value: boolean) => {
                originalIsEnabled(value);
            }
        });

        this.isDisposable = true;
        this.undisposeProperties.push('context');
        this.validationFunctions = [];
    }

    public static async createInstanceAsync<T extends IControlFactoryConstructorParameter>(
        item: IDVGItemDTO,
        context: Context,
        additionalOptions: T
    ): Promise<{ controlFactory?: ControlFactory, tuple?: ControlFactoryTuple }> {
        if (context?.isDisposed !== false)
            return {};

        const controlFactory = Factory.createInstance(item.uiGenerator, {
            detailViewItem: item,
            context,
            ...additionalOptions
        }, 'DummyControl') as ControlFactory;

        const tuple = AsyncTypeGuards.isAsyncTupleProvider<ControlFactoryTuple>(controlFactory)
            ? await controlFactory.getTupleAsync()
            : controlFactory.getTuple();

        if (!context.isDisposed) {
            context.setCreatedTuple(item.name, tuple);
            context.setCreatedFactory(controlFactory);
        }

        return { controlFactory, tuple };
    }

    /**
    * Registriert eine Standardvalidierung
    */
    public registerDefaultValidation(props: Array<string>): void {
        if (this.context.onValidationError != null) {
            this.context.onValidationError.push((fieldName: string, message: string, valid: boolean) => {

                //CFGFI-37: Keine Validierungen für disposed Factories
                if (this.isDisposed) {
                    return;
                }

                // Wenn das Control valide ist, wird dieses durch einen Reset im DetailViewContext ausgelöst
                // In diesem Fall muss keine Namensprüfung durchgeführt werden
                if (valid || props.indexOf(fieldName) !== -1) {
                    this.isValid(valid);
                    this.invalidationText(message);
                }

                this.isValidByServer = valid;
            });

            this.context.genericObject.onChange(() => {
                this.isValid(true);
                this.invalidationText('');
            }, props, this);
        }
    }

    /** 
     * Entfernt des Assoziationsnamen aus einem Order-Statement für die Verwendung in Standard-Iteratoren
     */
    public convertOrderStatementWithoutAssociation(orderBy: string): string {
        if (orderBy == null) {
            return null;
        }

        return orderBy.replace(new RegExp('this.' + this.detailViewItem.listView.relationName + '.', 'g'), 'this.');
    }

    /** 
     * Registriert eine Validierung auf Basis eines Observables
     */
    public registerObservableValidation(observable: KnockoutObservable<any>): void {
        if (this.context.onValidationError != null) {
            this.context.onValidationError.push((fieldName: string, message: string, valid: boolean) => {
                if (valid || (this.detailViewItem && fieldName === this.detailViewItem.name)) {
                    this.isValid(valid);
                    this.invalidationText(message);
                }
                this.isValidByServer = valid;
            });

            this.defineSubscription(observable, () => {
                this.isValid(true);
                this.invalidationText('');
            });
        }
    }

    /** 
     * Registriert eine Typvalidierung auf dem Wert eines Observables, aus welchem normalerweise der Wert des Properties entsteht
     */
    public registerTypePreValidation(property: string, observable: KnockoutObservable<any>, validator: Function): void {
        const execValidator = (value) => {
            return validator(value, this.detailViewItem.dotNetTypeName);
        };

        this.defineSubscription(observable, (value: any) => {
            this.callClientValidation(true);
        });

        this.validationFunctions.push({
            value: observable,
            validate: execValidator
        });
    }

    /** 
     * Registriert eine Typvalidierung auf dem Wert eines Properties
     */
    public registerTypeValidation(property: string, validator: Function): void {
        const execValidator = (value) => {
            return validator(value, this.detailViewItem.dotNetTypeName);
        };

        this.context.genericObject.onChange((prop: string, value: any) => {
            this.callClientValidation(true);
        }, [property], this, true);

        this.validationFunctions.push({
            value: this.context.genericObject.getObservable(property),
            validate: execValidator
        });
    }

    /** 
     * Registriert eine Custom-Validierung
     */
    public registerCustomValidation(props: Array<string>, observable: KnockoutObservable<any>, func: (value: any) => boolean, validationMessageFunc?: (valueToValidate: any) => string): void {
        this.defineSubscription(observable, (value: any) => {
            this.callClientValidation(true);
        });

        this.validationFunctions.push({
            value: observable,
            validate: func,
            validationText: validationMessageFunc
        });
    }

    /** 
     * Fügt ein Icon zur Wiederherstellung des Derived-Wertes hinzu, falls notwendig
     */
    public addOverwritableToControl(control: JQuery<HTMLElement>, props: Array<string>, tooltipErgName?: string): void {
        if (this.detailViewItem.isDerivedOverwritable === true) {
            let locator = LocatorHelper.buildLocator(this.viewModel.locator, 'revert');
            let viewModel = new this.LinkViewModel({
                isEnabled: this.isEnabled,
                parentLocator: this.locator,
                locator: locator,
                tooltip: new this.LabelViewModel({
                    ergName: tooltipErgName || 'REVERT_BUTTON_TOOLTIP'
                }),
                image: new this.ImageViewModel({
                    parentLocator: this.locator,
                    locator: LocatorHelper.buildLocator(locator, 'image'),
                    source: GlobalConfiguration.inlineImages.RESET,
                    alternativeText: new this.LabelViewModel({
                        ergName: tooltipErgName || 'REVERT_BUTTON_TOOLTIP'
                    })
                }),
                isToolBarLink: true,
                onClick: () => {
                    for (let i = 0; i < props.length; i++) {
                        const observable = this.context.genericObject.getObservable(props[i]);
                        if (Utilities.isNullOrUndefined(observable))
                            continue;
                        
                        // ist der Wert bereits 'null', so würde 'observable(null);' kein Re-Trigger etwaiger Subscriptions auslösen, da sich der Wert nicht ändern würde
                        // deshalb stoßen wir in dem Fall eine erneute Berechnung über valueHasMutated an
                        if (observable() === null)
                            observable.valueHasMutated();
                        else 
                            observable(null);
                    }

                    // Wir müssen hier die Client-seitige Validierung in der aktuellen Factory für den Moment deaktivieren!
                    // Grund: Ist das Control invalide, so gibt es einen Konflikt zwischen 'pendingChanges' und dem invaliden Control.
                    // Da wir uns hier im Reset-Fall befinden, macht es wenig Sinn, mögliche invalide Controls beizubehalten, da durch den Reset ein neuer Wert
                    // durch den Server geliefert wird.
                    this.ignoreClientValidation(true);
                    try {
                        this.context.updateProperties();
                    } finally {
                        this.ignoreClientValidation(false);
                    }
                }
            });

            let link = new this.Link(viewModel);
            let tuple = link.getTuple();

            tuple.control.appendTo(control);
        }
    }

    /**
     * Ruft die clientseitigen Validierungen, die für dieses Control registriert wurden, auf
     * @param resetServerValidation Gibt an, ob die serverseitige Validierungsmeldung zurückgesetzt werden soll (Default: false)
     */
    public callClientValidation(resetServerValidation: boolean = false): void {
        if (!resetServerValidation && !this.isValidByServer)
            return;
        
        if (this.ignoreClientValidation()) {
            this.isValid(true);
            return;
        }
        
        let isValid = true;
        let message = null;
        for (let x = 0; x < this.validationFunctions.length; x++) {
            const value = this.validationFunctions[x].value();
            isValid = this.validationFunctions[x].validate(value);
            if (!isValid) {
                if (Utilities.isFunction(this.validationFunctions[x].validationText))
                    message = this.validationFunctions[x].validationText(value);

                break;
            }
        }

        if (isValid) {
            this.isValid(true);
            this.invalidationText('');
        } else {
            this.isValid(false);
            this.invalidationText(message ?? ResourceManager.getInstance().get('PROPERTY_CONSTRAINT'));
        }
    }

    /** 
     * Standardimplementierung zum Auslesen der Default-Operators
     */
    public getAvailableOperators(): Array<{ergName: string, value: string}> {
        let result = [];
        const typeName = this.detailViewItem.dotNetTypeName;
        const isString = TypeConverter.typeIsChar(typeName) || TypeConverter.typeIsString(typeName);
        const isNumber = TypeConverter.typeIsNumber(typeName);
        const isDateTime = TypeConverter.typeIsDateTime(typeName);
        const isBoolean = TypeConverter.typeIsBool(typeName);
        const isByte = TypeConverter.typeIsByte(typeName);

        if (isString) {
            result.push({ergName: 'LIKE', value: 'LIKE'});
            result.push({ ergName: 'STARTS_WITH', value: 'STARTS_WITH' });
        }

        result.push({ ergName: 'EQUALS', value: '=' });

        if (!isBoolean)
            result.push({ ergName: 'NOT_EQUALS', value: '!=' });

        if (isNumber || isDateTime || isByte)
            result.push({ ergName: 'GREATER_EQUALS', value: '>=' });

        if (isDateTime)
            result.push({ ergName: 'GREATER', value: '>' });

        if (isNumber || isDateTime || isByte)
            result.push({ ergName: 'LESS_EQUALS', value: '<=' });

        if (isDateTime) 
            result.push({ ergName: 'LESS', value: '<' });
        

        return result;
    }

    /**
     * Liefert den Wert für das FilterViewModel
     */
    public getValueForFilter(): any {
        return '';
    }

    public getFilterInfo(): FilterInfo {
        return {
            field: '',
            value: ''
        };
    }

    /**
     * Setzt den Wert / die Werte des ViewModels zurück
     */
    public resetValue() {
        this.viewModel.resetValue();
    }

    /**
    * Erzeugt das FactoryTuple und die Factory
    */
    public getTuple(): ControlFactoryTuple {
        if (this.tuple)
            return this.tuple;

        const $control = this.getControl();
        const $label = this.getLabel();
        this.tuple = new ControlFactoryTuple(this, $control, $label);
        return this.tuple;
    }

    public getControl(): JQuery<HTMLElement> {
        throw new Error('Die Funktion "getControl" wurde nicht implementiert');
    }

    /**
     * Standardimplementierung, welche bei Bedarf überschrieben werden kann
     */
    public getLabel(): JQuery<HTMLElement> {
        let label = null;

        if (this.detailViewItem != null) {
            this.labelViewModel = new this.LabelViewModel({
                ergName: this.detailViewItem.ergName,
                parentLocator: this.locator,
                locator: this.detailViewItem.name + '.default-label',
                postfix: new this.LabelViewModel({
                    caption: this.defineComputed(() => {
                        if (this.isRequired() === true && this.isSummaryViewMode === false) {
                            return ' *';
                        }

                        return '';
                    })
                })
            });

            if (this.control) {
                let labelElement = $('<label>');
                this.control.addLabelReference(labelElement);
                this.label = new this.Label(this.labelViewModel, labelElement);
            }
            else
                this.label = new this.Label(this.labelViewModel);

            let tuple = this.label.getTuple();
            label = tuple.control;
        } else {
            label = $();
        }

        return label;
    }

    public getErgNameForValidationMessage(): string {
        return this.detailViewItem.ergName;
    }

    /**
    * Disposed die ControlFactory
    */
    protected disposeInternal(): void {
        if (this.tuple != null && this.tuple.isDisposed === false) {
            this.tuple.dispose();
        }
        this.tuple = null;

        super.disposeInternal();
    }
}